import { NgIf } from '@angular/common';
import { Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges, ViewEncapsulation, inject } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { ActivatedRoute, Router, RouterOutlet } from '@angular/router';
import { FuseFullscreenComponent } from '@fuse/components/fullscreen';
import { FuseLoadingBarComponent } from '@fuse/components/loading-bar';
import { FuseNavigationItem, FuseNavigationService, FuseVerticalNavigationComponent } from '@fuse/components/navigation';
import { FuseMediaWatcherService } from '@fuse/services/media-watcher';
import { TranslocoModule } from '@ngneat/transloco';
import { demoEmails } from 'app/app.const';
import { AuthService } from 'app/core/auth/auth.service';
import { AdminService } from 'app/core/datas/admin.service';
import { UserDataService } from 'app/core/datas/user-data';
import { Company, Site, User } from 'app/core/models/base.model';
import { NavigationService } from 'app/core/navigation/navigation.service';
import { Navigation } from 'app/core/navigation/navigation.types';
import { UserComponent } from 'app/layout/common/user/user.component';
import { LanguagesComponent } from 'app/layout/common/languages/languages.component';
import { compactNavigation } from 'app/mock-api/common/navigation/data';
import { environment } from 'environments/environment';
import { Subject, takeUntil } from 'rxjs';

@Component({
    selector: 'compact-layout',
    templateUrl: './compact.component.html',
    encapsulation: ViewEncapsulation.None,
    imports: [FuseLoadingBarComponent, MatButtonModule, MatIconModule, UserComponent, LanguagesComponent, NgIf, RouterOutlet, FuseVerticalNavigationComponent, TranslocoModule]
})
export class CompactLayoutComponent implements OnInit, OnChanges, OnDestroy {
    private _authSvc = inject(AuthService);
    private _userDataSvc = inject(UserDataService);
    private _adminSvc = inject(AdminService);

    isScreenSmall: boolean;
    // navigation: Navigation;
    navigation: FuseNavigationItem[] = [];

    private _unsubscribeAll: Subject<any> = new Subject<any>();
    appVersion: string = environment.appVersion;
    debug: boolean = !environment.production;
    hasAppPerm: boolean = false;
    user: User;
    @Input() siteid: number;
    @Input() site: Company;  // Changed from Site to Company for site→company architecture
    /**
     * Constructor
     */
    constructor(
        private _activatedRoute: ActivatedRoute,
        private _router: Router,
        private _navigationService: NavigationService,
        private _fuseMediaWatcherService: FuseMediaWatcherService,
        private _fuseNavigationService: FuseNavigationService,
    ) {

    }

    // -----------------------------------------------------------------------------------------------------
    // @ Accessors
    // -----------------------------------------------------------------------------------------------------

    /**
     * Getter for current year
     */
    get currentYear(): number {
        return new Date().getFullYear();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Lifecycle hooks
    // -----------------------------------------------------------------------------------------------------

    /**
     * On changes — re-render nav when parent pushes refreshed site data
     */
    ngOnChanges(changes: SimpleChanges): void {
        if (changes['site'] && !changes['site'].firstChange && this.site) {
            this.loadSiteMenu();
        }
    }

    /**
     * On init
     */
    ngOnInit(): void {
        console.log('compact siteid:', this.siteid);
        this.user = this._userDataSvc.getUser();
        this.hasAppPerm = this._authSvc.hasBasicRole('app_owner');

        // Check if site was provided as Input (from AdminLayoutComponent)
        if (this.site) {
            // Site provided as Input - use it directly
            console.log('compact site (from Input):', this.site);
            this.loadSiteMenu();
        } else {
            // No Input - subscribe to AdminService for general layout usage
            // this._adminSvc.companySite$.subscribe((site: any) => {
            //     console.log('compact site (from AdminService):', site);
            //     this.site = site as Company;
            //     if (this.site) {
            //         this.loadSiteMenu();
            //     } else {
            //         this.loadDefaultMenu();
            //     }
            // });
            this.loadDefaultMenu();
        }

        // Subscribe to media changes
        this._fuseMediaWatcherService.onMediaChange$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe(({ matchingAliases }) => {
                // Check if the screen is small
                this.isScreenSmall = !matchingAliases.includes('md');
            });
    }

    /**
     * Load menu from site settings
     */
    private loadSiteMenu(): void {
        let companyMenus = [];
        let siteMenus = [];
        try {
            // Company allow-list lives on the parent company's appSettings
            companyMenus = this.site.parent.appSettings['com.aspevo.itrace.company']['menu'] ?? [];
        } catch {}
        try {
            siteMenus = this.site.appSettings['com.aspevo.itrace.site']['menu'] ?? [];
        } catch {}
        this.loadMenuFromSite(siteMenus, companyMenus);
    }

    loadRemoteMenu(menus) {
        console.log('menus:', menus);

        let newNavigation: FuseNavigationItem[] = [];
        menus.forEach(parent => {
            if (!parent.hidden) {
                let parentNav: FuseNavigationItem = {
                    id: parent.id,
                    title: parent.title,
                    subtitle: parent.subtitle ? parent.subtitle : '',
                    type: parent.type,
                    icon: parent.icon,
                    link: parent.link,
                    children: []
                }
                if (parent.children) {
                    parent.children.forEach(child => {
                        if (!child.hidden) {
                            let childNav: FuseNavigationItem = {
                                id: child.id,
                                title: child.title,
                                subtitle: child.subtitle ? child.subtitle : '',
                                type: child.type,
                                icon: child.icon,
                                link: child.link,
                            }
                            parentNav.children.push(childNav);
                        }
                    });
                }


                newNavigation.push(parentNav)
            }
        });
        this.navigation = newNavigation;
    }

    loadMenuFromSite(menus, companyMenus = []) {
        let newNavigation: FuseNavigationItem[] = [];

        // If no site menu and no company menu, fall back to defaults
        if (menus.length === 0 && companyMenus.length === 0) {
            this.loadDefaultMenu();
            return;
        }

        compactNavigation.forEach((nav: FuseNavigationItem) => {
            // Clone to avoid mutating the shared compactNavigation singleton
            const navCopy: FuseNavigationItem = { ...nav };

            // Company gate: if company has explicitly disabled this item, skip it entirely
            const companyNav = companyMenus.find(x => x.id === nav.id);
            if (companyNav?.hidden === true) {
                return;
            }

            if (nav.id == 'apps' && demoEmails.includes(this.user.email)) {
                // skip for demo users
            } else if (nav.id === 'setting') {
                // Settings always shown unless company explicitly locked it (already handled above)
                const siteNav = menus.find(x => x.id === nav.id);
                if (!siteNav || !siteNav.hidden) {
                    newNavigation.push(navCopy);
                }
            } else {
                const siteNav = menus.find(x => x.id === nav.id);
                if (!siteNav || !siteNav.hidden) {
                    newNavigation.push(navCopy);
                }

                if (nav.children) {
                    const children: FuseNavigationItem[] = [];
                    nav.children.forEach(childNav => {
                        if (childNav.id === 'apps.hierarchy' || childNav.id === 'apps.distribution') {
                            return;
                        }
                        // Company gate for children
                        const companyChildNav = companyMenus.find(x => x.id === childNav.id);
                        if (companyChildNav?.hidden === true) {
                            return;
                        }
                        const siteChildNav = menus.find(x => x.id === childNav.id);
                        if (!siteChildNav || !siteChildNav.hidden) {
                            children.push(childNav);
                        }
                    });
                    navCopy.children = children;
                }
            }
        });
        this.navigation = newNavigation;
        // let project = this._userDataSvc.getSelectedProject(this.site.project.id);

        // let projectMenus = [];
        // try {
        //     projectMenus = project.appSettings['com.aspevo.itrace.site']['menu'];
        // } catch (error) {
        //     console.log('error:', error);
        //     console.log('project:', project);
        //     projectMenus = [];
        // }
        // compactNavigation.forEach((nav: FuseNavigationItem) => {

        //     if (nav.id == 'apps' && demoEmails.includes(this.user.email)) {

        //     } else {
        //         let projectNav = projectMenus.find(x => x.id == nav.id);
        //         if (projectNav && !projectNav.hidden) {
        //             console.log('p menu:', nav.id, projectNav.hidden)
        //             let siteNav = menus.find(x => x.id == nav.id);

        //             if (siteNav) {
        //                 if (!siteNav.hidden) {
        //                     newNavigation.push(nav);
        //                 }
        //             } else {
        //                 newNavigation.push(nav);
        //             }
        //             console.log('nav.children 1:', nav.children);
        //             if (nav.children) {
        //                 let children: FuseNavigationItem[] = [];
        //                 nav.children.forEach(childNav => {
        //                     // console.log('childNav:', childNav);
        //                     let skip = false;
        //                     if (childNav.id == 'apps.aggregation' || childNav.id == 'apps.distribution') {
        //                         skip = true
        //                     }
        //                     if (!skip && projectNav.children) {
        //                         let projectChildNav = projectNav.children.find(x => x.id == childNav.id);
        //                         if (projectChildNav && !projectChildNav.hidden) {
        //                             // console.log('p child menu:', nav.id, projectNav.hidden)
        //                             let siteChildNav = menus.find(x => x.id == childNav.id);
        //                             // console.log('p siteChildNav:', siteChildNav.id, siteChildNav.hidden)
        //                             if (siteChildNav) {
        //                                 if (!siteChildNav.hidden) {
        //                                     children.push(childNav);
        //                                 }
        //                             } else {
        //                                 children.push(childNav);
        //                             }
        //                         }
        //                     }

        //                 });

        //                 nav.children = children;
        //             }

        //             console.log('nav.children 2:', nav.children);
        //         }
        //     }


        // });

        // console.log('newNavigation:', newNavigation);

    }

    loadDefaultMenu() {
        let newNavigation: FuseNavigationItem[] = [];

        compactNavigation.forEach((element: FuseNavigationItem) => {
            // element.link = `/${this.siteid}` + element.link;

            if (element.id === 'team' || element.id === 'groups') {
                if (this._userDataSvc.hasRoles(['OWN', 'STA', 'SUV'])) {
                    newNavigation.push(element);
                }
            } else if (element.id === 'setting') {
                newNavigation.push(element);
            } else {
                if (element.id == 'apps' && demoEmails.includes(this.user.email)) {

                } else {
                    newNavigation.push(element);
                }

            }
        });

        this.navigation = newNavigation;
    }

    /**
     * Check if user has access to settings


    /**
     * On destroy
     */
    ngOnDestroy(): void {
        // Unsubscribe from all subscriptions
        this._unsubscribeAll.next(null);
        this._unsubscribeAll.complete();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Toggle navigation
     *
     * @param name
     */
    toggleNavigation(name: string): void {
        // Get the navigation
        const navigation = this._fuseNavigationService.getComponent<FuseVerticalNavigationComponent>(name);

        if (navigation) {
            // Toggle the opened status
            navigation.toggle();
        }
    }

    onLogoClick(): void {
        this._navigationService.hijackLogoNavigation();
    }
}
